import cx from 'classnames'

import { focusRingStyles } from '../../libs/focusRingStyles'
import { typographyStyles } from '../../structure/typography/styles'

type ButtonStylesArgs = {
  disabled: boolean
  /**
   * Use for destructive actions - normally as a primary or secondary button
   */
  isDanger: boolean
  isFullWidth: boolean
  /**
   * - `Primary` submit/apply/confirm actions
   * - `Secondary` less important actions
   * - `Discreet` non-form actions of less importance - i.e. links/navigation
   * - `Text` similar to discreet but when the action is inline with text
   */
  variant: 'primary' | 'secondary' | 'discreet' | 'text'
  hasCustomPadding?: boolean
}

export const buttonStyles = ({
  isDanger,
  isFullWidth,
  variant,
  disabled,
  hasCustomPadding,
}: ButtonStylesArgs) => {
  const SPACE_NON_TEXT = hasCustomPadding ? '' : 'px-2 py-1.5'

  const variantStyles = {
    primary: cx(!disabled && 'shadow-sm hover:shadow-md', SPACE_NON_TEXT),
    secondary: cx(
      'bg-current/10',
      !disabled && 'hover:bg-current/20 active:bg-current/30',
      SPACE_NON_TEXT,
    ),
    discreet: cx(
      SPACE_NON_TEXT,
      !disabled && 'hover:bg-current/10 active:bg-current/20',
    ),
    text: cx(
      !disabled &&
        cx(
          'before:content-[""] before:absolute before:-inset-y-0.5 before:-inset-x-1 before:rounded-md hover:before:bg-current/10 active:before:bg-current/20', // we use before pseudo-element to create the background effect to not mess with negative margins
          'before:pointer-events-none', // to ensure we don't interfere with e.g. tooltips
        ),
    ),
  }[variant]

  const variantWithDangerStyles = {
    primary: cx(
      'bg-primary text-primary-50',
      !disabled &&
        'shadow-primary-600/25 hover:bg-primary-600 active:bg-primary-700',
    ),
    primary_isDanger: cx(
      'bg-error text-error-50',
      !disabled && 'shadow-error-600/25 hover:bg-error-600 active:bg-error-700',
    ),
    secondary: cx(
      'inset-ring-primary',
      primaryTextWithHoverStyles({ disabled }),
    ),
    secondary_isDanger: cx(
      'inset-ring-error',
      errorTextWithHoverStyles({ disabled }),
    ),
    discreet: cx(primaryTextWithHoverStyles({ disabled })),
    discreet_isDanger: cx(errorTextWithHoverStyles({ disabled })),
    text: cx(primaryTextWithHoverStyles({ disabled })),
    text_isDanger: cx(errorTextWithHoverStyles({ disabled })),
  }[`${variant}${isDanger ? '_isDanger' : ''}` as const]

  return cx(
    'relative items-center justify-center flex-nowrap whitespace-nowrap transition outline-none rounded-lg',
    'max-w-full', // ensure buttons are never wider than their parent container
    disabled ? 'opacity-50 cursor-not-allowed' : cx('cursor-pointer'),
    isFullWidth ? 'flex w-full' : 'inline-flex',
    variantStyles,
    variantWithDangerStyles,
    focusRingStyles({
      hasError: isDanger,
      variant: variant === 'text' ? 'pseudo' : 'default',
    }),
    typographyStyles(),
  )
}

export const primaryTextWithHoverStyles = ({
  disabled,
}: {
  disabled: boolean
}) =>
  cx(
    'text-primary',
    !disabled && 'hover:text-primary-600 dark:hover:text-primary-400',
  )

const errorTextWithHoverStyles = ({ disabled }: { disabled: boolean }) =>
  cx(
    'text-error',
    !disabled && 'hover:text-error-600 dark:hover:text-error-400',
  )
